"use client";

import { c as _c } from "react/compiler-runtime";
import { usePathname } from "next/navigation";
import { CommandPaletteProvider } from "@/components/command-palette";
import { ConnectPanelProvider } from "@/components/connect-panel/connect-panel-provider";
import { PortalSidebar } from "@/components/portal-sidebar";
import { PORTAL_CANVAS_CLASS, PORTAL_INSET_CLASS, PORTAL_MOBILE_TRIGGER_CLASS } from "@/components/portal-shell-style";
import dynamic from "next/dynamic";
import { AssistantPanelFab, AssistantPanelProvider, useAssistantPanel } from "@/components/dashboard-agent/AssistantPanelProvider";

// Lazy: the pane pulls in the whole chat renderer (streamdown, tool displays),
// which must not ride every dashboard route's bundle for users who never open it.
const DashboardAgentPane = dynamic(() => import("@/components/dashboard-agent/DashboardAgentPane").then(module => module.DashboardAgentPane), {
  ssr: false
});
import { SidebarInset, SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { useAuth } from "@/components/AuthProvider";
import { useChatPath } from "@/components/chat-ui-provider";
import { MFA_PAGE_PATH } from "@/lib/auth/mfa-policy";

/**
 * Route-aware chrome for the authenticated app area: the one portal sidebar
 * (portal IA overhaul — sections for every role, account menu in its footer)
 * on the left, content on the right. There is no second navigation layer
 * anywhere: the admin console and the member pages share this sidebar.
 *
 * Logged-out pages (e.g. /login, which lives inside this route group) render
 * bare — no chrome, and so does the two-factor challenge (/mfa): the session
 * behind it is not usable yet, so a sidebar full of pages it cannot open
 * would mislead. The web-chat surface (legacy) renders its own history
 * sidebar + inset via ChatContainer, so it opts out here.
 */
export function MainShell(t0) {
  const $ = _c(8);
  const {
    children
  } = t0;
  const pathname = usePathname();
  const chatPath = useChatPath();
  const {
    user
  } = useAuth();
  if (!user || pathname === MFA_PAGE_PATH) {
    let t1;
    if ($[0] !== children) {
      t1 = <>{children}</>;
      $[0] = children;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let t1;
  if ($[2] !== chatPath || $[3] !== children || $[4] !== pathname) {
    t1 = pathname === chatPath ? children : <AssistantPanelProvider><SidebarProvider defaultOpen={true} className={PORTAL_CANVAS_CLASS}><PortalSidebar /><SidebarInset className={PORTAL_INSET_CLASS}><header className="pointer-events-none fixed top-4 left-4 z-40 md:hidden"><SidebarTrigger className={PORTAL_MOBILE_TRIGGER_CLASS} /></header>{children}</SidebarInset><AssistantPanelSlot /></SidebarProvider><AssistantPanelFab /></AssistantPanelProvider>;
    $[2] = chatPath;
    $[3] = children;
    $[4] = pathname;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const shell = t1;
  let t2;
  if ($[6] !== shell) {
    t2 = <ConnectPanelProvider><CommandPaletteProvider>{shell}</CommandPaletteProvider></ConnectPanelProvider>;
    $[6] = shell;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}

/**
 * Dashboard assistant (AIR-663): nothing mounts until the panel is first
 * opened; after that it stays mounted (hidden via CSS while closed) so the
 * conversation survives toggling.
 */
function AssistantPanelSlot() {
  const $ = _c(1);
  const {
    hasOpened
  } = useAssistantPanel();
  if (!hasOpened) {
    return null;
  }
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <DashboardAgentPane />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}